import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Server, Globe, Mail, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function PrivacyPage() {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-emerald-950">
            {/* Header */}
            <div className="bg-slate-900/50 border-b border-emerald-500/20">
                <div className="max-w-4xl mx-auto px-4 py-4">
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/')}
                        className="text-emerald-400 hover:text-emerald-300"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Home
                    </Button>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 py-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-8"
                >
                    {/* Title */}
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500/20 rounded-full mb-4">
                            <Shield className="w-8 h-8 text-emerald-400" />
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                            Privacy Policy
                        </h1>
                        <p className="text-emerald-300/80">
                            Last updated: December 2025
                        </p>
                    </div>

                    {/* Privacy Commitment */}
                    <div className="bg-slate-800/50 rounded-xl p-6 border border-emerald-500/20">
                        <div className="flex items-start gap-4">
                            <Lock className="w-6 h-6 text-emerald-400 mt-1 flex-shrink-0" />
                            <div>
                                <h2 className="text-xl font-semibold text-white mb-3">Our Privacy Commitment</h2>
                                <p className="text-slate-300 leading-relaxed">
                                    We are committed to respecting your privacy and protecting the security of any information we obtain from you. Our philosophy is simple: <strong className="text-emerald-400">Your thoughts are your own.</strong>
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Data Collection */}
                    <div className="bg-slate-800/50 rounded-xl p-6 border border-emerald-500/20">
                        <div className="flex items-start gap-4">
                            <Eye className="w-6 h-6 text-emerald-400 mt-1 flex-shrink-0" />
                            <div>
                                <h2 className="text-xl font-semibold text-white mb-3">Data Collection</h2>
                                <p className="text-slate-300 leading-relaxed mb-4">
                                    <strong className="text-emerald-400">We do NOT collect or store:</strong>
                                </p>
                                <ul className="space-y-2 text-slate-300">
                                    <li className="flex items-center gap-2">
                                        <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                                        Your conversation content or chat history on our servers
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                                        Personal identification information
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                                        Location data or device identifiers
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                                        Browsing history or tracking cookies
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Health Data Section - Required by Google Play */}
                    <div className="bg-amber-900/30 rounded-xl p-6 border border-amber-500/30">
                        <div className="flex items-start gap-4">
                            <Shield className="w-6 h-6 text-amber-400 mt-1 flex-shrink-0" />
                            <div>
                                <h2 className="text-xl font-semibold text-white mb-3">Health & Wellness Data</h2>
                                <p className="text-slate-300 leading-relaxed mb-4">
                                    IulianAI is a wellness companion application that provides emotional support and psychological guidance through AI-powered conversations.
                                </p>
                                <p className="text-slate-300 leading-relaxed mb-4">
                                    <strong className="text-amber-400">Health-related information you may share:</strong>
                                </p>
                                <ul className="space-y-2 text-slate-300 mb-4">
                                    <li className="flex items-start gap-2">
                                        <span className="w-2 h-2 bg-amber-500 rounded-full mt-2"></span>
                                        <span>Emotional states, feelings, and mental wellness concerns you choose to discuss</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-2 h-2 bg-amber-500 rounded-full mt-2"></span>
                                        <span>Personal challenges, stress, anxiety, or other psychological topics</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-2 h-2 bg-amber-500 rounded-full mt-2"></span>
                                        <span>Life situations that affect your mental or emotional wellbeing</span>
                                    </li>
                                </ul>
                                <p className="text-slate-300 leading-relaxed mb-4">
                                    <strong className="text-emerald-400">How we handle this data:</strong>
                                </p>
                                <ul className="space-y-2 text-slate-300">
                                    <li className="flex items-start gap-2">
                                        <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></span>
                                        <span><strong>NOT stored on servers:</strong> Your conversations are processed in real-time and are NOT saved on our servers</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></span>
                                        <span><strong>NOT shared with third parties:</strong> We do not sell, share, or transfer your health data to anyone</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></span>
                                        <span><strong>Local storage only:</strong> Chat history exists only on your device and can be deleted anytime</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></span>
                                        <span><strong>AI processing:</strong> Messages are sent to Google's Gemini AI for generating responses, subject to Google's privacy policies</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Local Storage */}
                    <div className="bg-slate-800/50 rounded-xl p-6 border border-emerald-500/20">
                        <div className="flex items-start gap-4">
                            <Server className="w-6 h-6 text-emerald-400 mt-1 flex-shrink-0" />
                            <div>
                                <h2 className="text-xl font-semibold text-white mb-3">Local Storage</h2>
                                <p className="text-slate-300 leading-relaxed">
                                    Chat history is stored <strong className="text-emerald-400">only on your device</strong> using browser local storage. This data never leaves your device and is not transmitted to our servers. You can clear this data at any time through your browser settings.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Access Codes */}
                    <div className="bg-slate-800/50 rounded-xl p-6 border border-emerald-500/20">
                        <div className="flex items-start gap-4">
                            <Mail className="w-6 h-6 text-emerald-400 mt-1 flex-shrink-0" />
                            <div>
                                <h2 className="text-xl font-semibold text-white mb-3">Access Codes & Payments</h2>
                                <p className="text-slate-300 leading-relaxed">
                                    When you purchase access, we collect only the email address you provide for delivery of your access code. Payment processing is handled securely by Stripe, and we do not store your payment card details.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* GDPR */}
                    <div className="bg-slate-800/50 rounded-xl p-6 border border-emerald-500/20">
                        <div className="flex items-start gap-4">
                            <Globe className="w-6 h-6 text-emerald-400 mt-1 flex-shrink-0" />
                            <div>
                                <h2 className="text-xl font-semibold text-white mb-3">GDPR & Your Rights</h2>
                                <p className="text-slate-300 leading-relaxed">
                                    We respect global privacy standards. Because we do not store personal data or chat histories, your "Right to be Forgotten" and "Right to Access" are exercised automatically by the transient nature of our system.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Contact */}
                    <div className="bg-emerald-900/30 rounded-xl p-6 border border-emerald-500/30 text-center">
                        <h2 className="text-xl font-semibold text-white mb-3">Questions?</h2>
                        <p className="text-slate-300 mb-4">
                            If you have any questions about this Privacy Policy, please contact us:
                        </p>
                        <a 
                            href="mailto:contact@iulianai.online"
                            className="text-emerald-400 hover:text-emerald-300 font-medium"
                        >
                            contact@iulianai.online
                        </a>
                    </div>

                    {/* Company Info */}
                    <div className="text-center text-slate-400 text-sm pt-8 border-t border-slate-700">
                        <p className="mb-2">
                            <strong>TRIBOIAI LLC</strong>
                        </p>
                        <p>
                            1021 E Lincolnway Suite 9733, Cheyenne, WY 82001, USA
                        </p>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
